import { Component } from "react";
import { Card, Button, Row, Col, Alert } from "react-bootstrap";
import { FaCheck } from "react-icons/fa";
import api from "../../services/api";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Editor from '@monaco-editor/react';
import regrasObrigatoriasFarmacovigilancia from './regrasObrigatoriasFarmacovigilancia.json';

class ProgramaConfiguracoes extends Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            saving: false,
            error: '',
            configuracoesOriginal: '{}',
            configuracoesJson: '{}',
            configuracoesJsonError: '',
            configuracoesValidationErrors: [],
        };
    }

    componentDidMount() {
        this.carregarConfiguracoes();
    }

    carregarConfiguracoes = () => {
        const { programaId } = this.props;

        if (!programaId) {
            this.setState({ loading: false, error: 'ID do programa não informado' });
            return;
        }

        this.setState({ loading: true, error: '' });

        api.get(`/programas/${programaId}/`)
            .then((response) => {
                const configuracoes = response.data.configuracoes;
                const configuracoesJson = configuracoes ? JSON.stringify(configuracoes, null, 4) : '{}';

                this.setState({
                    configuracoesOriginal: configuracoesJson,
                    configuracoesJson,
                    loading: false,
                    error: '',
                    configuracoesJsonError: '',
                    configuracoesValidationErrors: [],
                });
            })
            .catch((error) => {
                let erroMsg = 'Erro ao carregar configurações do programa';

                if (error.response && error.response.status === 500) {
                    erroMsg = 'Servidor indisponível no momento, tente novamente';
                } else if (error.response && error.response.status === 403) {
                    erroMsg = 'Você não tem permissão para acessar essa área';
                }

                this.setState({
                    loading: false,
                    error: erroMsg,
                });
            });
    };

    handleConfiguracoesJsonChange = (value) => {
        this.setState({
            configuracoesJson: value || '',
            configuracoesJsonError: '',
            configuracoesValidationErrors: [],
        });
    };

    validarJson = () => {
        try {
            const configuracoes = JSON.parse(this.state.configuracoesJson);
            this.setState({ configuracoesJsonError: '' });
            return configuracoes;
        } catch (e) {
            this.setState({ configuracoesJsonError: 'JSON inválido. Verifique a sintaxe.' });
            return null;
        }
    };

    escapeHtml = (value) => {
        return String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    };

    getValueByPath = (obj, path) => {
        if (!path) return undefined;

        return path.split('.').reduce((acc, key) => {
            if (acc === null || acc === undefined) return undefined;
            return acc[key];
        }, obj);
    };

    // Reúne as regras de campos obrigatórios que serão validadas:
    // 1) as declaradas no próprio JSON do programa (configuracoes_meta.required_fields);
    // 2) as regras obrigatórias CENTRALIZADAS (regrasObrigatoriasFarmacovigilancia.json),
    //    aplicadas automaticamente a qualquer programa cuja configuração contenha a chave
    //    "farmacovigilancia". Essas últimas não podem ser desligadas pelo usuário: têm
    //    precedência por path e são sempre forçadas como required.
    getRegrasCamposObrigatorios = (configuracoesObj) => {
        const regrasDoPrograma = Array.isArray(configuracoesObj?.configuracoes_meta?.required_fields)
            ? configuracoesObj.configuracoes_meta.required_fields
            : [];

        const temFarmacovigilancia = !!configuracoesObj
            && Object.prototype.hasOwnProperty.call(configuracoesObj, 'farmacovigilancia');

        const regrasObrigatorias = temFarmacovigilancia
            ? (regrasObrigatoriasFarmacovigilancia.required_fields || []).map((regra) => ({ ...regra, required: true }))
            : [];

        // Mescla por path: as regras obrigatórias entram por último para ter precedência.
        const regrasPorPath = new Map();
        regrasDoPrograma.forEach((regra) => { if (regra?.path) regrasPorPath.set(regra.path, regra); });
        regrasObrigatorias.forEach((regra) => { if (regra?.path) regrasPorPath.set(regra.path, regra); });

        return Array.from(regrasPorPath.values());
    };

    validarCamposObrigatorios = (configuracoesObj) => {
        const requiredFields = this.getRegrasCamposObrigatorios(configuracoesObj);

        if (requiredFields.length === 0) {
            this.setState({ configuracoesValidationErrors: [] });
            return [];
        }

        const errors = requiredFields
            .filter((field) => field?.required)
            .map((field) => {
                const value = this.getValueByPath(configuracoesObj, field.path);
                const label = field.label || field.path || 'Campo obrigatório';
                const validation = field.validation || 'non_empty';

                let isValid = true;

                if (validation === 'list_non_empty') {
                    isValid = Array.isArray(value)
                        && value.length > 0
                        && value.every((item) => String(item ?? '').trim() !== '');
                } else if (Array.isArray(value)) {
                    isValid = value.length > 0;
                } else {
                    isValid = String(value ?? '').trim() !== '';
                }

                return isValid ? null : label;
            })
            .filter(Boolean);

        this.setState({ configuracoesValidationErrors: errors });
        return errors;
    };

    formatDiffItems = (items) => {
        if (!Array.isArray(items) || items.length === 0) {
            return '<li>Nenhuma alteração</li>';
        }

        return items
            .map((item) => {
                if (typeof item === 'string') {
                    return `<li>${this.escapeHtml(item)}</li>`;
                }

                return `<li><pre style="white-space: pre-wrap; margin: 0;">${this.escapeHtml(JSON.stringify(item, null, 2))}</pre></li>`;
            })
            .join('');
    };

    buildDiffHtml = (configuracoesDiff = {}) => {
        return `
            <div class="text-start">
                <p>Revise as alterações abaixo antes de salvar:</p>
                <div class="mb-3">
                    <strong>Adicionados</strong>
                    <ul>${this.formatDiffItems(configuracoesDiff.added)}</ul>
                </div>
                <div class="mb-3">
                    <strong>Alterados</strong>
                    <ul>${this.formatDiffItems(configuracoesDiff.changed)}</ul>
                </div>
                <div class="mb-0">
                    <strong>Removidos</strong>
                    <ul>${this.formatDiffItems(configuracoesDiff.removed)}</ul>
                </div>
            </div>
        `;
    };

    confirmarAlteracoesConfiguracoes = async (configuracoesDiff) => {
        const result = await Swal.fire({
            title: 'Confirmar alterações',
            icon: 'warning',
            html: this.buildDiffHtml(configuracoesDiff),
            width: 900,
            showCancelButton: true,
            confirmButtonText: 'Confirmar e salvar',
            cancelButtonText: 'Cancelar',
            focusConfirm: false,
        });

        return result.isConfirmed;
    };

    salvarConfiguracoesRequest = (programaId, configuracoesObj, payloadExtra = {}) => {
        return api.put(`/programas/${programaId}/`, {
            configuracoes: configuracoesObj,
            ...payloadExtra,
        });
    };

    tratarErroSalvar = async (error, programaId, configuracoesObj) => {
        const responseData = error?.response?.data;
        const status = error?.response?.status;

        if (status === 409 && responseData?.requires_confirmation) {
            const confirmou = await this.confirmarAlteracoesConfiguracoes(responseData.configuracoes_diff);

            if (!confirmou) {
                return false;
            }

            await this.salvarConfiguracoesRequest(programaId, configuracoesObj, {
                confirmar_alteracoes_configuracoes: true,
            });

            return true;
        }

        if (status === 400 && responseData?.configuracoes) {
            const erroConfiguracoes = Array.isArray(responseData.configuracoes)
                ? responseData.configuracoes.join('\n')
                : String(responseData.configuracoes);

            this.setState({ configuracoesJsonError: erroConfiguracoes });
        }

        throw error;
    };

    salvarConfiguracoes = async () => {
        const configuracoesObj = this.validarJson();

        if (!configuracoesObj) {
            return;
        }

        const validationErrors = this.validarCamposObrigatorios(configuracoesObj);
        if (validationErrors.length > 0) {
            this.setState({
                configuracoesJsonError: 'Existem campos obrigatórios críticos sem preenchimento.',
            });
            return;
        }

        const { programaId } = this.props;

        this.setState({ saving: true });

        try {
            const { data: previewData } = await this.salvarConfiguracoesRequest(programaId, configuracoesObj, {
                preview_config_changes: true,
            });

            if (previewData?.requires_confirmation) {
                const confirmou = await this.confirmarAlteracoesConfiguracoes(previewData.configuracoes_diff);

                if (!confirmou) {
                    this.setState({ saving: false });
                    return;
                }
            }

            await this.salvarConfiguracoesRequest(programaId, configuracoesObj, {
                confirmar_alteracoes_configuracoes: true,
            });

            const MySwal = withReactContent(Swal);
            MySwal.fire({
                title: <p>Configurações salvas com sucesso</p>,
                icon: 'success',
                showCloseButton: true,
            });

            this.setState({
                saving: false,
                configuracoesOriginal: JSON.stringify(configuracoesObj, null, 4),
                configuracoesValidationErrors: [],
                configuracoesJsonError: '',
            });
        } catch (error) {
            let erroMsg = 'Erro ao salvar configurações, tente novamente';

            try {
                const recovered = await this.tratarErroSalvar(error, programaId, configuracoesObj);

                if (recovered) {
                    const MySwal = withReactContent(Swal);
                    MySwal.fire({
                        title: <p>Configurações salvas com sucesso</p>,
                        icon: 'success',
                        showCloseButton: true,
                    });

                    this.setState({
                        saving: false,
                        configuracoesOriginal: JSON.stringify(configuracoesObj, null, 4),
                        configuracoesValidationErrors: [],
                        configuracoesJsonError: '',
                    });
                    return;
                }
            } catch (handledError) {
                const finalError = handledError;

                if (finalError.response && finalError.response.status === 500) {
                    erroMsg = 'Servidor indisponível no momento';
                } else if (finalError.response && finalError.response.status === 400) {
                    const campos = finalError.response.data;
                    if (campos.configuracoes) {
                        this.setState({ configuracoesJsonError: campos.configuracoes });
                    }
                } else if (finalError.response && finalError.response.data?.detail) {
                    erroMsg = String(finalError.response.data.detail);
                }

                const MySwal = withReactContent(Swal);
                MySwal.fire({
                    icon: 'error',
                    showCloseButton: true,
                    title: <p>{erroMsg}</p>,
                });

                this.setState({ saving: false });
                return;
            }

            if (error.response && error.response.status === 500) {
                erroMsg = 'Servidor indisponível no momento';
            } else if (error.response && error.response.status === 400) {
                const campos = error.response.data;
                if (campos.configuracoes) {
                    this.setState({ configuracoesJsonError: campos.configuracoes });
                }
            } else if (error.response && error.response.data?.detail) {
                erroMsg = String(error.response.data.detail);
            }

            const MySwal = withReactContent(Swal);
            MySwal.fire({
                icon: 'error',
                showCloseButton: true,
                title: <p>{erroMsg}</p>,
            });

            this.setState({ saving: false });
        }
    };

    render() {
        const {
            loading,
            saving,
            error,
            configuracoesJson,
            configuracoesJsonError,
            configuracoesValidationErrors,
        } = this.state;

        if (loading) {
            return (
                <Card className="cardDestaque">
                    <Card.Body>
                        <Row>
                            <Col xs={12}>
                                <p>Carregando...</p>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            );
        }

        if (error) {
            return (
                <Card className="cardDestaque">
                    <Card.Body>
                        <Alert variant="warning">{error}</Alert>
                    </Card.Body>
                </Card>
            );
        }

        return (
            <Card className="cardDestaque">
                <Card.Body>
                    <Row className="align-items-center mb-3">
                        <Col sm={6} lg={6}>
                            <h4>Configurações do Programa</h4>
                        </Col>
                    </Row>

                    {configuracoesJsonError && (
                        <Alert variant="danger" className="py-2 mb-3">{configuracoesJsonError}</Alert>
                    )}

                    {configuracoesValidationErrors.length > 0 && (
                        <Alert variant="warning" className="py-2 mb-3">
                            <div>Campos obrigatórios críticos pendentes:</div>
                            <ul className="mb-0 mt-2 ps-3">
                                {configuracoesValidationErrors.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        </Alert>
                    )}

                    <Editor
                        height="500px"
                        defaultLanguage="json"
                        value={configuracoesJson}
                        onChange={this.handleConfiguracoesJsonChange}
                        theme="vs-dark"
                        options={{
                            minimap: { enabled: false },
                            fontSize: 14,
                            formatOnPaste: true,
                            formatOnType: true,
                            automaticLayout: true,
                            tabSize: 4,
                        }}
                    />

                    <Button
                        variant="success"
                        className="mt-3"
                        onClick={this.salvarConfiguracoes}
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                            <>
                                <FaCheck className="me-1" /> Salvar Configurações
                            </>
                        )}
                    </Button>
                </Card.Body>
            </Card>
        );
    }
}

export default ProgramaConfiguracoes;
